import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { finalize, forkJoin, Subject, takeUntil } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { PlanMeetingModalComponent } from '../plan-meeting-modal/plan-meeting-modal.component';
import { TStudentTab } from '../student-tabs/student-tabs.component';
import { EExamKey } from '@gray/domain/enums/exam-key.enum';
import { ILevel } from '@gray/domain/models/user/level.model';
import { IExam } from '@gray/domain/models/exam/exam.model';
import { examShortName } from '@gray/domain/helpers/exam.helper';
import { CoachStudentService } from '../../../core/services/coach-student.service';
import { CoachCatalogService } from '../../../core/services/coach-catalog.service';
import { CoachPeriodService } from '../../../core/services/coach-period.service';
import { CoachTaskService } from '../../../core/services/coach-task.service';
import { CoachChatService } from '../../../core/services/coach-chat.service';
import { IStudentExamOverview, IStudentOverview } from '../../../core/models/student-overview.model';

/** Tabs a link can open with `?tab=`; the disabled exams tab is left out. */
const LINKABLE_TABS: TStudentTab[] = ['progress', 'planner', 'videos', 'weekly-report', 'meeting-notes', 'tasks', 'messages'];

/**
 * The header of the student selected on the roster: who they are, where they stand on both exams
 * and how the coaching has gone so far. The tabs underneath it arrive with the following phases.
 */
@Component({
  selector: 'coach-student-detail',
  templateUrl: './student-detail.component.html',
  styleUrl: './student-detail.component.scss',
})
export class StudentDetailComponent implements OnChanges, OnDestroy {
  /** Set by the roster; picking another student reloads this panel in place. */
  @Input() studentId!: number;

  student?: IStudentOverview;
  levels: ILevel[] = [];
  exams: IExam[] = [];

  /** Which exam the avatar colour, the level box and the overall progress follow. */
  examKey = EExamKey.TYT;
  examKeys = EExamKey;

  /** Which panel is open under the header. */
  activeTab: TStudentTab = 'progress';
  /** Filled by the task tab so its counter also shows on the tab bar. */
  taskProgress: string | null = null;
  /** Filled by the messages tab; the badge drops to zero once the thread is read. */
  unreadMessages = 0;

  isLoading = false;

  destroyed$ = new Subject();

  constructor(
    private studentService: CoachStudentService,
    private catalogService: CoachCatalogService,
    private periodService: CoachPeriodService,
    private taskService: CoachTaskService,
    private chatService: CoachChatService,
    private translate: TranslateService,
    private modalService: NgbModal,
    private toastrService: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.studentId) {
      return;
    }

    // Another student means another plan, another route, another history: start them on the first
    // tab rather than leaving a panel showing the previous student's data.
    if (changes['studentId'] && !changes['studentId'].firstChange) {
      this.activeTab = 'progress';
      this.taskProgress = null;
      this.unreadMessages = 0;
    }

    // A link can open a tab directly: /dashboard/students/:id?tab=messages
    const linkedTab = this.route.snapshot.queryParamMap.get('tab') as TStudentTab | null;
    if (linkedTab && LINKABLE_TABS.includes(linkedTab)) {
      this.activeTab = linkedTab;
    }

    this.getStudent();
    this.getTabBadges();
  }

  /**
   * The counters belong to tabs that are not rendered yet, so the header fetches them itself —
   * otherwise a coach only learns how many tasks or messages are waiting after opening the tab.
   */
  getTabBadges() {
    forkJoin({
      tasks: this.taskService.getTasks(this.studentId, '?page=1&size=1'),
      completedTasks: this.taskService.getTasks(this.studentId, '?page=1&size=1&filter[isCompleted]=true'),
      conversation: this.chatService.getConversation(this.studentId),
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => {
        const total = r.tasks.data?.meta?.total ?? 0;
        const completed = r.completedTasks.data?.meta?.total ?? 0;
        this.taskProgress = total ? `${completed}/${total}` : null;
        this.unreadMessages = r.conversation.data?.unreadCount ?? 0;
      });
  }

  getStudent() {
    // The header reads the period the students screen is on; levels and scores are stored per period.
    const periodId = this.periodService.selectedId;

    this.isLoading = true;
    forkJoin({
      student: this.studentService.getStudentOverview(this.studentId, periodId ? `?periodId=${periodId}` : ''),
      levels: this.catalogService.getLevels(),
      exams: this.catalogService.getExams(),
    })
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        this.student = r.student.data as IStudentOverview;
        this.levels = (r.levels.data?.result ?? r.levels.data ?? []) as ILevel[];
        this.exams = (r.exams.data?.result ?? r.exams.data ?? []) as IExam[];
      });
  }

  setExamKey(examKey: EExamKey) {
    this.examKey = examKey;
  }

  setTab(tab: TStudentTab) {
    this.activeTab = tab;
    // Keep the open tab in the URL so it can be shared and survives a reload.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {tab: tab === 'progress' ? null : tab},
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  activeExamId(): number | null {
    return this.exams.find(exam => exam.examKey === this.examKey)?.id ?? null;
  }

  /** Short form: the switch, the stat boxes and the route badge all read better as TYT / AYT. */
  examName(examKey: EExamKey): string {
    return examShortName(this.exams.find(exam => exam.examKey === examKey), examKey);
  }

  exam(): IStudentExamOverview | null {
    if (!this.student) {
      return null;
    }

    return this.examKey === EExamKey.AYT ? this.student.proficiency : this.student.base;
  }

  levelKey(): string {
    return this.exam()?.level?.key ?? 'default';
  }

  levelName(): string | null {
    const key = this.exam()?.level?.key;
    return key ? this.translate.instant(`levels.${key}`) : null;
  }

  metaParts(): string[] {
    if (!this.student) {
      return [];
    }

    return [this.student.grade?.name, this.student.period?.name, this.student.department?.name]
      .filter(part => !!part);
  }

  /** Position on the whole scale of the selected exam, read from the per-exam level ranges. */
  overallPercent(): number {
    const score = this.exam()?.score;
    const examId = this.activeExamId();
    if (score === null || score === undefined || !examId) {
      return 0;
    }

    const ranges = this.levels
      .map(level => level.examScores?.find(examScore => examScore.examId === examId))
      .filter(range => !!range);
    if (!ranges.length) {
      return 0;
    }

    const min = Math.min(...ranges.map(range => range.minScore ?? 0));
    const max = Math.max(...ranges.map(range => range.maxScore ?? range.minScore ?? 0));
    if (max <= min) {
      return 0;
    }

    return Math.max(0, Math.min(100, Math.round(((score - min) / (max - min)) * 100)));
  }


  /** Planning happens in place: the dialog offers the coach's own free slots for this student. */
  planMeeting() {
    const modalRef = this.modalService.open(PlanMeetingModalComponent, {
      size: 'md',
      centered: true,
      animation: true,
      // A whole day of 15-minute slots is longer than the viewport.
      scrollable: true,
    });
    modalRef.componentInstance.studentId = this.student?.id;
    modalRef.componentInstance.studentName = this.student?.name;
    modalRef.componentInstance.levelKey = this.levelKey();

    modalRef.closed.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.toastrService.success(this.translate.instant('agenda.appointment_created'));
      this.getStudent();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
